import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Typography } from "antd";

import { recordAgenticManualConsentAPI } from "../../api/api";
import GeneralConsentForm from "../../component/Patient/ConsentQuickActionForms/GeneralConsentForm";
import { CONSENT_ACTION_TYPES } from "../../constant/consent";
import { GRAY_600 } from "../../constant/colors";

const { Text } = Typography;

/**
 * "Record consent" on a consent_decide task (MVP-31413): the patient already
 * consented outside TailorMed, so instead of sending a link the navigator
 * records that consent and the agent carries on to enrollment. The form IS the
 * Manage Consent popup's record form — same "how was it received", same
 * effective-date window, same details + attachment — so a consent recorded
 * here reads identically in the patient's consent history.
 *
 * One call: FN records the consent the way Manage Consent does, then answers
 * the agent's task as "recorded from this task", which is what the audit shows.
 *
 * `onResolved()` closes the task in the host.
 */
const RecordConsentModal = ({ open, patientId, agenticRunId, onCancel, onResolved, onError }) => {
  const { t } = useTranslation();
  // The form hands `onSave` no response, so whether the agent was reached is
  // carried across the two callbacks here.
  const agentNotifiedRef = useRef(null);

  const recordConsent = async (formData) => {
    const response = await recordAgenticManualConsentAPI(patientId, agenticRunId, formData);
    agentNotifiedRef.current = response?.data?.agentNotified;
  };

  const finishWhenAgentReached = () => {
    if (agentNotifiedRef.current === false) {
      onError(t("agentic_board.errors.consent_recorded_agent_not_reached"));
      return;
    }
    onResolved();
  };

  return (
    <Modal
      title={t("agentic_board.record_consent.title")}
      open={open}
      footer={null}
      onCancel={onCancel}
      width={600}
      destroyOnClose
      data-testid="agentic-board-record-consent-modal"
    >
      <Text style={{ display: "block", marginBottom: 12, color: GRAY_600, fontSize: 13 }}>
        {t("agentic_board.record_consent.intro")}
      </Text>
      <GeneralConsentForm
        actionType={CONSENT_ACTION_TYPES.RECEIVED}
        onSubmit={recordConsent}
        onSave={finishWhenAgentReached}
        onCancel={onCancel}
        // The form clears its own banner with `onError(null)` before each
        // submit — the hub has no banner to clear, only toasts to raise.
        onError={(errorMessage) => errorMessage && onError(errorMessage)}
      />
    </Modal>
  );
};

export { RecordConsentModal };
